import { useState, useRef, useEffect } from 'react';
import { Card, Group, Stack, Text, Badge, TextInput, Button, ScrollArea, Loader, Center, Switch } from '@mantine/core';
import { IconSend, IconHeadset } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { EmptyState } from '../../components/EmptyState';
import { TableToolbar } from '../../components/TableToolbar';
import { useTranslation } from 'react-i18next';

interface Msg { id: string; fromSuperadmin: boolean; body: string; createdAt: string }
interface Tenant { id: string; name: string; slug: string }
interface Thread { tenant: Tenant; lastMessage: Msg | null; unread: number }

/** Superadmin — tenant adminlari bilan yozishma (faqat shu threadlar ko'rinadi). */
export default function SaasSupport() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const viewportRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Filtrlangan bo'sh natija ma'lumot umuman yo'qligidan farqli matn ko'rsatadi
  const filterActive = !!qDeb || unreadOnly;

  // Sahifalash SHART EMAS — threadlar soni tenantlar soniga teng (server tartibi: oxirgi xabar)
  const { data: threads = [], isLoading } = useQuery({
    queryKey: ['sa-support', qDeb, unreadOnly],
    queryFn: async () => (await api.get<Thread[]>('/superadmin/support', {
      params: { q: qDeb || undefined, unread: unreadOnly ? '1' : undefined },
    })).data,
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
  });

  const { data: thread } = useQuery({
    queryKey: ['sa-support-thread', activeId],
    queryFn: async () => (await api.get<{ tenant: Tenant; messages: Msg[] }>(`/superadmin/support/${activeId}`)).data,
    enabled: !!activeId,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (activeId && thread?.tenant?.id === activeId) qc.invalidateQueries({ queryKey: ['sa-support'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, thread?.tenant?.id, thread?.messages?.length]);

  const replyMut = useMutation({
    mutationFn: (body: string) => api.post(`/superadmin/support/${activeId}`, { body }),
    onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: ['sa-support-thread', activeId] }); qc.invalidateQueries({ queryKey: ['sa-support'] }); },
  });

  useEffect(() => { viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight }); }, [thread?.messages?.length]);

  return (
    <>
      <PageHeader title={t('saasSupportPage.title')} subtitle={t('saasSupportPage.subtitle')} />
      {/* Saralash BERILMAYDI — threadlar doim oxirgi xabar bo'yicha tartiblanadi (server) */}
      <TableToolbar search={search} onSearch={setSearch} searchPlaceholder={t('saasSupportPage.search')}>
        <Switch
          mb={8}
          label={t('saasSupportPage.unreadOnly')}
          checked={unreadOnly}
          onChange={(e) => setUnreadOnly(e.currentTarget.checked)}
        />
      </TableToolbar>
      <Group align="flex-start" gap="md" wrap="nowrap">
        {/* Threadlar ro'yxati ham sarlavhali kartada — suhbat paneli bilan bir xil */}
        <SectionCard title={t('common.list')} icon={IconHeadset} p="sm" w={320} style={{ flexShrink: 0 }}>
          {isLoading ? <Center py="xl"><Loader /></Center> : !threads.length ? (
            <EmptyState
              icon={IconHeadset}
              title={filterActive ? t('common.noMatch') : t('saasSupportPage.emptyTitle')}
              description={filterActive ? undefined : t('saasSupportPage.emptyDesc')}
            />
          ) : (
            <Stack gap="xs">
              {threads.map((th) => (
                <Card key={th.tenant.id} p="sm" withBorder={activeId === th.tenant.id}
                  style={{ cursor: 'pointer', borderColor: activeId === th.tenant.id ? 'var(--mantine-color-brand-5)' : undefined }}
                  onClick={() => setActiveId(th.tenant.id)}>
                  <Group justify="space-between" wrap="nowrap">
                    <div style={{ minWidth: 0 }}>
                      <Group gap={6}><Text fw={600} size="sm" truncate>{th.tenant.name}</Text><Badge size="xs" variant="light" color="grape">{th.tenant.slug}</Badge></Group>
                      <Text size="xs" c="dimmed" truncate>{th.lastMessage ? (th.lastMessage.fromSuperadmin ? t('saasSupportPage.youPrefix') : '') + th.lastMessage.body : ''}</Text>
                    </div>
                    {th.unread > 0 && <Badge size="sm" circle color="red">{th.unread}</Badge>}
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </SectionCard>

        {/* Suhbat — sarlavha boshqa kartalardagi kabi header'da (tanlanmaganda ham ko'rinadi) */}
        <SectionCard
          title={activeId ? (thread?.tenant?.name ?? '') : t('saasSupportPage.title')}
          icon={IconHeadset}
          right={activeId ? <Badge variant="light" color="grape">{thread?.tenant?.slug}</Badge> : undefined}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 480 }}
          bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          {!activeId ? <Center style={{ flex: 1 }}><Text c="dimmed">{t('saasSupportPage.selectSchool')}</Text></Center> : (
            <>
              <ScrollArea style={{ flex: 1 }} viewportRef={viewportRef} mb="sm">
                <Stack gap="xs" p="xs">
                  {(thread?.messages ?? []).map((m) => (
                    <Card key={m.id} p="xs" maw="75%" style={{ alignSelf: m.fromSuperadmin ? 'flex-end' : 'flex-start', background: m.fromSuperadmin ? 'var(--mantine-color-brand-1)' : 'var(--mantine-color-gray-1)' }}>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{m.body}</Text>
                      <Text size="xs" c="dimmed" ta="right">{new Date(m.createdAt).toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>
                    </Card>
                  ))}
                </Stack>
              </ScrollArea>
              <Group gap="xs">
                <TextInput style={{ flex: 1 }} placeholder={t('saasSupportPage.replyPlaceholder')} value={text} onChange={(e) => setText(e.currentTarget.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) replyMut.mutate(text.trim()); }} />
                <Button leftSection={<IconSend size={16} />} loading={replyMut.isPending} disabled={!text.trim()} onClick={() => replyMut.mutate(text.trim())}>{t('saasSupportPage.send')}</Button>
              </Group>
            </>
          )}
        </SectionCard>
      </Group>
    </>
  );
}
